import { useEffect, useMemo, useState } from "react";
import {
  Plus, Eye, Pencil, RefreshCw, Trash2, X, Upload, FileText, Loader2,
  AlertTriangle, CheckCircle2, Power,
} from "lucide-react";
import toast from "react-hot-toast";
import { usePrdTemplateStore, type PrdTemplate } from "../store/usePrdTemplateStore";

// ── Settings → PRD Template Management ───────────────────────────────────────
//
// One template per parent category. The uploaded document's headings become
// the structure every generated PRD in that category follows.
//
// The table carries exactly four columns — Template Name, Category, Last
// Updated, Actions — as specified. Status, file type and section count are
// real and useful, so they ride inside the cells they belong to rather than
// becoming columns of their own.

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const kb = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

type FormState = { name: string; category: string; description: string; file: File | null };
const EMPTY: FormState = { name: "", category: "", description: "", file: null };

export default function AdminPrdTemplatesPage() {
  const {
    templates, categories, missingCategories, loading, busy,
    fetchAll, get, save, update, setActive, remove, openFile,
  } = usePrdTemplateStore();

  const [form, setForm] = useState<FormState | null>(null);
  const [editing, setEditing] = useState<PrdTemplate | null>(null);
  const [viewing, setViewing] = useState<PrdTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PrdTemplate | null>(null);
  // Replace reuses the add form, pre-aimed at a category.
  const [replacing, setReplacing] = useState<PrdTemplate | null>(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const working = Boolean(busy);
  const byCategory = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.category, t])),
    [templates]
  );

  const openAdd = (category = "") => { setReplacing(null); setForm({ ...EMPTY, category }); };
  const openReplace = (t: PrdTemplate) => {
    setReplacing(t);
    setForm({ name: t.name, category: t.category, description: t.description, file: null });
  };

  const submitForm = async () => {
    if (!form) return;
    if (!form.name.trim()) { toast.error("Give the template a name."); return; }
    if (!form.category) { toast.error("Choose the parent category this template is for."); return; }
    if (!form.file) { toast.error("Upload the template file."); return; }
    const ok = await save({
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim(),
      file: form.file,
    });
    if (ok) { setForm(null); setReplacing(null); }
  };

  const submitEdit = async () => {
    if (!editing) return;
    const ok = await update(editing._id, {
      name: editing.name.trim(),
      category: editing.category,
      description: editing.description,
    });
    if (ok) setEditing(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-on-surface">PRD Template Management</h3>
          <p className="text-[13px] text-on-surface-variant mt-0.5 max-w-2xl">
            One template per parent category. The headings in the uploaded document become the structure of
            every PRD generated for that category — the project supplies the content.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openAdd()}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:brightness-110 flex items-center gap-1.5 shrink-0"
        >
          <Plus size={15} /> Add Template
        </button>
      </div>

      {/* A category with nothing configured blocks PRD generation for every
          project in it, so it is said here rather than discovered by a BD. */}
      {missingCategories.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-[13px] font-semibold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle size={14} /> No template configured for {missingCategories.length} categor
            {missingCategories.length === 1 ? "y" : "ies"}
          </p>
          <p className="text-[12px] text-amber-800 mt-1">
            Projects in {missingCategories.join(", ")} cannot generate a PRD until a template is added.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {missingCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => openAdd(c)}
                className="px-2.5 py-1 text-[11px] font-bold bg-amber-600 text-white rounded-lg hover:brightness-110"
              >
                Add {c} template
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-outline-variant overflow-hidden bg-surface-container-lowest">
        <table className="w-full text-left">
          <thead className="bg-surface-container-low">
            <tr className="text-[11px] uppercase tracking-wider font-bold text-on-surface-variant">
              <th className="px-4 py-3">Template Name</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Last Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-on-surface-variant">
                <Loader2 size={18} className="animate-spin inline" />
              </td></tr>
            )}

            {!loading && templates.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-[13px] text-on-surface-variant">
                No templates configured yet. Add one for each category you create projects in.
              </td></tr>
            )}

            {templates.map((t) => (
              <tr key={t._id} className="border-t border-outline-variant align-top">
                <td className="px-4 py-3">
                  <span className="text-[13px] font-semibold text-on-surface">{t.name}</span>
                  <span className="block text-[11px] text-on-surface-variant mt-0.5">
                    {t.sectionCount} section{t.sectionCount === 1 ? "" : "s"}
                    {t.file ? ` · ${t.file.fileName}` : " · built-in structure, no file uploaded"}
                    {!t.active && " · inactive"}
                  </span>
                  {!t.active && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800 text-[10px] font-bold">
                      Not in use — generation blocked for this category
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[12px] font-medium text-on-surface">{t.category}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[12px] text-on-surface">{fmt(t.updatedAt)}</span>
                  {t.updatedByName && (
                    <span className="block text-[11px] text-on-surface-variant">by {t.updatedByName}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn title="View" onClick={async () => { const full = await get(t._id); if (full) setViewing(full); }}>
                      <Eye size={15} />
                    </IconBtn>
                    <IconBtn title="Edit" onClick={() => setEditing({ ...t })}>
                      <Pencil size={15} />
                    </IconBtn>
                    <IconBtn title="Replace file" onClick={() => openReplace(t)}>
                      <RefreshCw size={15} />
                    </IconBtn>
                    <IconBtn
                      title={t.active ? "Deactivate" : "Reactivate"}
                      onClick={() => setActive(t._id, !t.active)}
                      disabled={working}
                    >
                      <Power size={15} className={t.active ? "" : "text-emerald-600"} />
                    </IconBtn>
                    <IconBtn title="Delete" danger onClick={() => setConfirmDelete(t)}>
                      <Trash2 size={15} />
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Which of the six categories are covered. */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map((c) => {
          const t = byCategory[c];
          const ok = t && t.active;
          return (
            <span
              key={c}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"
              }`}
            >
              {ok ? <CheckCircle2 size={11} className="inline mr-1" /> : <AlertTriangle size={11} className="inline mr-1" />}
              {c}
            </span>
          );
        })}
      </div>

      {/* ── Add / Replace ── */}
      {form && (
        <Modal
          title={replacing ? `Replace the ${replacing.category} template` : "Add PRD Template"}
          onClose={() => { setForm(null); setReplacing(null); }}
        >
          <div className="space-y-3.5">
            {replacing && (
              <p className="text-[12px] rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2">
                PRDs generated from now on will use the new file. PRDs that already exist are not changed or
                regenerated.
              </p>
            )}

            <Field label="Template Name" required>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Website Development PRD"
                className={inputCls}
              />
            </Field>

            <Field label="Parent Category" required>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                disabled={Boolean(replacing)}
                className={inputCls}
              >
                <option value="">Select the category…</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                    {byCategory[c] && c !== replacing?.category ? " — already configured (this will replace it)" : ""}
                  </option>
                ))}
              </select>
              {!replacing && byCategory[form.category] && (
                <span className="block text-[11px] text-amber-700 mt-1">
                  {form.category} already has "{byCategory[form.category]?.name}". Saving replaces it.
                </span>
              )}
            </Field>

            <Field label="Template File" required>
              <label className="flex items-center gap-2 px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-[13px] cursor-pointer hover:bg-surface-container">
                <Upload size={14} />
                {form.file ? form.file.name : "Choose a Word (.docx) or PDF file"}
                <input
                  type="file"
                  accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                />
              </label>
              <span className="block text-[11px] text-on-surface-variant mt-1">
                The document's headings become the PRD's sections, so style the section titles as Heading 1 or
                Heading 2. Up to 10MB. Scanned PDFs cannot be read.
              </span>
            </Field>

            <Field label="Description">
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional — what this template is for"
                className={inputCls}
              />
            </Field>
          </div>

          <ModalFooter
            onCancel={() => { setForm(null); setReplacing(null); }}
            onConfirm={submitForm}
            busy={busy === "save"}
            confirmLabel={replacing ? "Replace template" : "Save template"}
          />
        </Modal>
      )}

      {/* ── Edit (no file) ── */}
      {editing && (
        <Modal title="Edit template" onClose={() => setEditing(null)}>
          <div className="space-y-3.5">
            <Field label="Template Name" required>
              <input
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Parent Category" required>
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className={inputCls}
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <textarea
                rows={2}
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className={inputCls}
              />
            </Field>
            <p className="text-[11px] text-on-surface-variant">
              To change the document's structure, use <strong>Replace</strong> — the sections come from the file.
            </p>
          </div>
          <ModalFooter onCancel={() => setEditing(null)} onConfirm={submitEdit} busy={busy === "update"} confirmLabel="Save changes" />
        </Modal>
      )}

      {/* ── View ── */}
      {viewing && (
        <Modal title={viewing.name} onClose={() => setViewing(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
              <Detail label="Parent Category" value={viewing.category} />
              <Detail label="Status" value={viewing.active ? "Active" : "Inactive — generation blocked"} />
              <Detail label="Source file" value={viewing.file ? `${viewing.file.fileName} · ${kb(viewing.file.size)}` : "None — built-in structure"} />
              <Detail label="Uploaded" value={viewing.file ? fmt(viewing.file.uploadedAt) : "—"} />
              <Detail label="Last updated" value={`${fmt(viewing.updatedAt)}${viewing.updatedByName ? ` by ${viewing.updatedByName}` : ""}`} />
              <Detail label="PRDs generated from it" value={String(viewing.usage?.prdCount ?? 0)} />
            </div>

            {viewing.description && (
              <p className="text-[12.5px] text-on-surface-variant border-l-2 border-outline-variant pl-3">
                {viewing.description}
              </p>
            )}

            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-on-surface-variant mb-1.5">
                Structure — {viewing.sectionCount} sections, in this order
              </p>
              <ol className="space-y-1">
                {viewing.sections.map((s, i) => (
                  <li key={s.key} className="text-[12.5px] text-on-surface flex items-baseline gap-2">
                    <span className="text-on-surface-variant w-5 shrink-0">{i + 1}.</span>
                    <span>
                      {s.title}
                      {s.required && <span className="ml-1.5 text-[10px] font-bold text-amber-700">REQUIRED</span>}
                      {s.commercial && <span className="ml-1.5 text-[10px] font-bold text-violet-700">COMMERCIAL</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {viewing.file && (
              <button
                type="button"
                onClick={() => openFile(viewing._id, viewing.file!.fileName)}
                disabled={working}
                className="px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-[12px] font-semibold hover:bg-surface-container flex items-center gap-1.5 disabled:opacity-50"
              >
                <FileText size={13} /> Open the source file
              </button>
            )}

            {!!viewing.audit?.length && (
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-on-surface-variant mb-1.5">History</p>
                <ul className="space-y-1">
                  {viewing.audit.slice(0, 10).map((a: any, i: number) => (
                    <li key={i} className="text-[11.5px] text-on-surface-variant">
                      <span className="font-semibold text-on-surface capitalize">{String(a.action).replace(/-/g, " ")}</span>
                      {a.byName ? ` · ${a.byName}` : ""} · {fmt(a.at)}
                      {a.result && a.result !== "ok" ? ` · ${a.result}` : ""}
                      {a.note ? <span className="block opacity-80">{a.note}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <ModalFooter onCancel={() => setViewing(null)} cancelLabel="Close" />
        </Modal>
      )}

      {/* ── Delete ── */}
      {confirmDelete && (
        <Modal title="Delete template" onClose={() => setConfirmDelete(null)}>
          <p className="text-[13px] text-on-surface-variant">
            Delete <strong>{confirmDelete.name}</strong>? {confirmDelete.category} projects will not be able to
            generate a PRD until another template is configured.
          </p>
          <p className="text-[12px] text-on-surface-variant mt-2">
            If PRDs have already been generated from it, it is deactivated instead of deleted so those records
            keep resolving.
          </p>
          <ModalFooter
            onCancel={() => setConfirmDelete(null)}
            onConfirm={async () => { await remove(confirmDelete._id); setConfirmDelete(null); }}
            busy={busy === "delete"}
            confirmLabel="Delete"
            danger
          />
        </Modal>
      )}
    </div>
  );
}

// ── Small shared pieces ──────────────────────────────────────────────────────

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary outline-none text-[13px]";

const IconBtn = ({
  title, onClick, children, danger, disabled,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${
      danger ? "text-rose-600 hover:bg-rose-50" : "text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-[12px] font-semibold text-on-surface mb-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
  </div>
);

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div>
    <span className="block text-[10px] uppercase tracking-wider font-bold text-on-surface-variant">{label}</span>
    <span className="text-on-surface">{value}</span>
  </div>
);

const Modal = ({
  title, onClose, children, wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
    <div
      className={`bg-surface-container-lowest w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[88vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-outline-variant shrink-0">
        <h3 className="text-[15px] font-bold text-on-surface">{title}</h3>
        <button type="button" onClick={onClose} className="p-1 text-on-surface-variant hover:bg-surface-container rounded-full">
          <X size={17} />
        </button>
      </div>
      <div className="px-5 py-4 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const ModalFooter = ({
  onCancel, onConfirm, busy, confirmLabel, cancelLabel = "Cancel", danger,
}: {
  onCancel: () => void;
  onConfirm?: () => void;
  busy?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}) => (
  <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-outline-variant">
    <button type="button" onClick={onCancel} className="px-4 py-2 text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg">
      {cancelLabel}
    </button>
    {onConfirm && (
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy}
        className={`px-4 py-2 text-[13px] font-bold text-white rounded-lg hover:brightness-110 disabled:opacity-50 flex items-center gap-1.5 ${
          danger ? "bg-rose-600" : "bg-primary"
        }`}
      >
        {busy && <Loader2 size={13} className="animate-spin" />}
        {confirmLabel}
      </button>
    )}
  </div>
);
